import React, { useEffect, useState, useCallback } from 'react';
import { Check, Pencil, XCircle, Clock, Send } from 'lucide-react';
import { toast } from 'sonner';
import { getAutoContent, editAutoContent, approveAutoContent, rejectAutoContent } from '../../utils/api';
import { StatusBadge, PlatformBadge } from './shared';

const inputCls =
  'w-full bg-black border border-divider rounded-lg px-3 py-2 text-white text-sm focus:border-lime focus:outline-none';

export const ApprovalQueue = ({ clientId, refreshKey, onChanged }) => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // {id, title, body}
  const [approving, setApproving] = useState(null); // item id showing schedule panel
  const [scheduleTime, setScheduleTime] = useState('');
  const [rejecting, setRejecting] = useState(null); // item id showing reason input
  const [rejectReason, setRejectReason] = useState('');

  const fetchItems = useCallback(async () => {
    setLoading(true);
    try {
      const res = await getAutoContent({ client_id: clientId, status: 'pending_approval' });
      setItems(res.data.items);
    } catch (err) {
      toast.error('Failed to load approval queue');
    } finally {
      setLoading(false);
    }
  }, [clientId]);

  useEffect(() => { fetchItems(); }, [fetchItems, refreshKey]);

  const saveEdit = async () => {
    try {
      await editAutoContent(editing.id, { title: editing.title, body: editing.body });
      toast.success('Draft updated');
      setEditing(null);
      fetchItems();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Edit failed');
    }
  };

  const approve = async (item, opts = {}) => {
    try {
      const res = await approveAutoContent(item.id, opts);
      const status = res.data.status;
      toast.success(
        status === 'published' ? `Published${res.data.publish_result?.sandbox ? ' (sandbox)' : ''}` :
        status === 'scheduled' ? 'Scheduled' : 'Approved'
      );
      setApproving(null);
      setScheduleTime('');
      fetchItems();
      onChanged();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Approve failed');
    }
  };

  const reject = async (item) => {
    try {
      await rejectAutoContent(item.id, { reason: rejectReason });
      toast.success('Rejected');
      setRejecting(null);
      setRejectReason('');
      fetchItems();
      onChanged();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Reject failed');
    }
  };

  if (loading) return <div className="text-gray py-12 text-center">Loading queue…</div>;
  if (items.length === 0)
    return (
      <div className="text-center py-16 border border-dashed border-divider rounded-xl" data-testid="queue-empty">
        <p className="text-gray-light font-medium">Approval queue is clear 🎉</p>
        <p className="text-gray text-sm mt-1">Generate content with AI and drafts will land here for review.</p>
      </div>
    );

  return (
    <div className="space-y-4" data-testid="approval-queue">
      {items.map((item) => (
        <div key={item.id} data-testid={`queue-item-${item.id}`} className="bg-card-bg border border-divider rounded-xl p-5">
          <div className="flex items-center gap-2 mb-3 flex-wrap">
            <PlatformBadge platform={item.platform} />
            <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-white/5 text-gray-light border border-divider capitalize">
              {item.type.replace('_', ' ')}
            </span>
            <StatusBadge status={item.status} />
            <span className="text-xs text-gray ml-auto">Topic: {item.topic}</span>
          </div>

          {editing?.id === item.id ? (
            <div className="space-y-3">
              <input data-testid={`edit-title-${item.id}`} className={inputCls} value={editing.title} onChange={(e) => setEditing({ ...editing, title: e.target.value })} />
              <textarea data-testid={`edit-body-${item.id}`} className={inputCls} rows={6} value={editing.body} onChange={(e) => setEditing({ ...editing, body: e.target.value })} />
              <div className="flex gap-2">
                <button data-testid={`edit-save-${item.id}`} onClick={saveEdit} className="px-4 py-1.5 rounded-lg bg-lime text-black text-sm font-bold">Save</button>
                <button onClick={() => setEditing(null)} className="px-4 py-1.5 rounded-lg text-gray-light text-sm">Cancel</button>
              </div>
            </div>
          ) : (
            <>
              <h4 className="text-white font-semibold text-lg mb-1">{item.title}</h4>
              <p className="text-gray-light text-sm whitespace-pre-wrap">{item.body}</p>
              {item.ad_details?.description && (
                <p className="text-gray text-xs mt-2">Description: {item.ad_details.description} · CTA: {item.ad_details.cta}</p>
              )}
              {item.edit_history?.length > 0 && (
                <p className="text-xs text-gray mt-2">✏️ Edited {item.edit_history.length} time(s)</p>
              )}
            </>
          )}

          {approving === item.id && (
            <div className="mt-4 p-4 bg-black rounded-lg border border-lime/30 space-y-3" data-testid={`approve-panel-${item.id}`}>
              <p className="text-sm text-white font-medium">When should this publish?</p>
              <div className="flex flex-wrap items-center gap-3">
                <button
                  data-testid={`publish-now-${item.id}`}
                  onClick={() => approve(item, { publish_now: true })}
                  className="px-4 py-2 rounded-lg bg-lime text-black text-sm font-bold flex items-center gap-2"
                >
                  <Send size={14} /> Publish Now
                </button>
                <span className="text-gray text-xs">or</span>
                <input
                  type="datetime-local"
                  data-testid={`schedule-input-${item.id}`}
                  className="bg-card-bg border border-divider rounded-lg px-3 py-2 text-white text-sm"
                  value={scheduleTime}
                  onChange={(e) => setScheduleTime(e.target.value)}
                />
                <button
                  data-testid={`schedule-confirm-${item.id}`}
                  onClick={() => scheduleTime ? approve(item, { scheduled_time: new Date(scheduleTime).toISOString() }) : toast.error('Pick a date/time')}
                  className="px-4 py-2 rounded-lg border border-lime text-lime text-sm font-bold flex items-center gap-2"
                >
                  <Clock size={14} /> Schedule
                </button>
                <button onClick={() => setApproving(null)} className="text-gray text-sm hover:text-white">Cancel</button>
              </div>
            </div>
          )}

          {rejecting === item.id && (
            <div className="mt-4 p-4 bg-black rounded-lg border border-orange/30 flex items-center gap-3" data-testid={`reject-panel-${item.id}`}>
              <input
                data-testid={`reject-reason-${item.id}`}
                className={inputCls}
                placeholder="Reason (optional)"
                value={rejectReason}
                onChange={(e) => setRejectReason(e.target.value)}
              />
              <button data-testid={`reject-confirm-${item.id}`} onClick={() => reject(item)} className="px-4 py-2 rounded-lg bg-orange/15 text-orange border border-orange/40 text-sm font-bold whitespace-nowrap">
                Confirm Reject
              </button>
              <button onClick={() => setRejecting(null)} className="text-gray text-sm hover:text-white">Cancel</button>
            </div>
          )}

          {editing?.id !== item.id && approving !== item.id && rejecting !== item.id && (
            <div className="flex gap-2 mt-4">
              <button
                data-testid={`approve-btn-${item.id}`}
                onClick={() => item.type === 'ad' ? approve(item) : setApproving(item.id)}
                className="px-4 py-2 rounded-lg bg-lime text-black text-sm font-bold flex items-center gap-2 hover:bg-lime/90"
              >
                <Check size={14} /> Approve
              </button>
              <button
                data-testid={`edit-btn-${item.id}`}
                onClick={() => setEditing({ id: item.id, title: item.title, body: item.body })}
                className="px-4 py-2 rounded-lg border border-divider text-gray-light text-sm flex items-center gap-2 hover:text-white hover:border-gray"
              >
                <Pencil size={14} /> Edit
              </button>
              <button
                data-testid={`reject-btn-${item.id}`}
                onClick={() => setRejecting(item.id)}
                className="px-4 py-2 rounded-lg border border-orange/40 text-orange text-sm flex items-center gap-2 hover:bg-orange/10"
              >
                <XCircle size={14} /> Reject
              </button>
            </div>
          )}
        </div>
      ))}
    </div>
  );
};
